import React, { useState } from 'react';
import { Button, Form, Message, Table } from 'semantic-ui-react';
import { client } from '../../shared/client';
import './recovery.scss';

type Sale = {
  id: number;
  sale_status: string;
  receipt_date?: string;
  fn?: string;
  fixed_seller_bonus?: number;
  admin_percent?: number;
  seller_first_name?: string;
  seller_last_name?: string;
  sn?: string;
};
type Preview = {
  sale: Sale;
  token: string;
  canApply: boolean;
  serviceReceipt: boolean;
  problems: string[];
  sellerProblems?: string[];
  adminProblems?: string[];
};
const display = (value: any) =>
  value == null || value === '' ? '—' : String(value);
const date = (value?: string) =>
  value ? value.replace('T', ' ').slice(0, 19) : '—';
const errorText = (error: any) =>
  (error.response && error.response.data && error.response.data.message) ||
  'Не удалось выполнить запрос. Обновите данные перед повторной попыткой.';

export default function SellerRecovery() {
  const [query, setQuery] = useState('');
  const [sales, setSales] = useState<Sale[]>([]);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [amount, setAmount] = useState('');
  const [adminAmount, setAdminAmount] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState('');
  const [searched, setSearched] = useState(false);
  const [reloadRequired, setReloadRequired] = useState(false);
  const base = '/service/sales/recovery';

  const load = async (id: number) => {
    setBusy(true);
    setError('');
    setResult('');
    setAmount('');
    setAdminAmount('');
    try {
      const response = await client.get(`${base}/seller/${id}`);
      setPreview(response.data);
      setReloadRequired(false);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  const search = async () => {
    setBusy(true);
    setError('');
    setSales([]);
    setSearched(false);
    setResult('');
    try {
      const response = await client.get(`${base}/search`, {
        params: { query: query.trim() },
      });
      setSales(response.data);
      setSearched(true);
      if (response.data.length === 1) await load(response.data[0].id);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  const apply = async () => {
    if (!preview || busy || !preview.canApply || reloadRequired) return;
    setBusy(true);
    setError('');
    setResult('');
    try {
      const response = await client.post(`${base}/seller/${preview.sale.id}`, {
        token: preview.token,
        amount,
        adminAmount,
      });
      setPreview(response.data);
      setResult(
        [
          amount ? `Продавцу начислено ${amount} баллов.` : '',
          adminAmount ? `АРТ начислено ${adminAmount} баллов.` : '',
          'Начисления сохранены в истории счетов.',
        ]
          .filter(Boolean)
          .join(' ')
      );
      setAmount('');
      setAdminAmount('');
    } catch (e) {
      setError(errorText(e));
      setReloadRequired(true);
    } finally {
      setBusy(false);
    }
  };
  const positiveAmount = (value: string) =>
    /^[1-9][0-9]*$/.test(value) && Number(value) <= 2147483647;
  const validAmount =
    (amount === '' || positiveAmount(amount)) &&
    (adminAmount === '' || positiveAmount(adminAmount)) &&
    (!!amount || !!adminAmount);

  return (
    <section className="sale-recovery seller-recovery">
      <h1>Исправление продажи продавца</h1>
      {error && <Message negative>{error}</Message>}
      {result && <Message positive>{result}</Message>}
      {!preview ? (
        <>
          <Form onSubmit={search}>
            <Form.Group>
              <Form.Input
                label="ID продажи или серийный номер"
                value={query}
                disabled={busy}
                maxLength={100}
                onChange={(_, data) => setQuery(String(data.value))}
              />
              <Form.Field className="search-action">
                <Button primary disabled={busy || !query.trim()} loading={busy}>
                  Найти
                </Button>
              </Form.Field>
            </Form.Group>
          </Form>
          {searched && !sales.length && <Message>Продажи не найдены.</Message>}
          {sales.length > 1 && (
            <Table compact celled>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Sale ID</Table.HeaderCell>
                  <Table.HeaderCell>Sale status</Table.HeaderCell>
                  <Table.HeaderCell />
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {sales.map((s) => (
                  <Table.Row key={s.id}>
                    <Table.Cell>{s.id}</Table.Cell>
                    <Table.Cell>{s.sale_status}</Table.Cell>
                    <Table.Cell>
                      <Button disabled={busy} onClick={() => load(s.id)}>
                        Открыть
                      </Button>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          )}
        </>
      ) : (
        <>
          <dl className="seller-recovery__fields">
            <dt>Sale ID:</dt>
            <dd>
              <strong>{preview.sale.id}</strong>
            </dd>
            <dt>Sale status:</dt>
            <dd>
              <strong
                className={
                  preview.sale.sale_status === 'FAIL'
                    ? 'sale-status--fail'
                    : preview.sale.sale_status === 'FINISH'
                    ? 'sale-status--finish'
                    : ''
                }
              >
                {preview.sale.sale_status}
              </strong>
            </dd>
            <dt>Receipt date:</dt>
            <dd>
              <strong>{date(preview.sale.receipt_date)}</strong>
            </dd>
            <dt
              className={
                preview.serviceReceipt ? 'receipt-fn--service' : undefined
              }
            >
              Receipt fn:
            </dt>
            <dd
              className={
                preview.serviceReceipt ? 'receipt-fn--service' : undefined
              }
            >
              <strong>{display(preview.sale.fn)}</strong>
            </dd>
            <dt>Total sale bonus:</dt>
            <dd>
              <strong>{display(preview.sale.fixed_seller_bonus)}</strong>
            </dd>
            <dt>Admin percent:</dt>
            <dd>
              <strong title="Текущий процент розничной точки, справочно. Количество баллов задаётся вручную.">
                {preview.sale.admin_percent == null
                  ? '—'
                  : `${preview.sale.admin_percent}% (текущий)`}
              </strong>
            </dd>
            <dt>Seller first name:</dt>
            <dd>
              <strong>{display(preview.sale.seller_first_name)}</strong>
            </dd>
            <dt>Seller last name:</dt>
            <dd>
              <strong>{display(preview.sale.seller_last_name)}</strong>
            </dd>
          </dl>
          {preview.problems.length > 0 && !result && (
            <Message warning list={preview.problems} />
          )}
          {!result &&
            preview.sellerProblems &&
            preview.sellerProblems.length > 0 && (
              <Message info list={preview.sellerProblems} />
            )}
          {!result &&
            preview.adminProblems &&
            preview.adminProblems.length > 0 && (
              <Message info list={preview.adminProblems} />
            )}
          <Form onSubmit={apply}>
            <Form.Input
              label="Количество баллов продавцу"
              value={amount}
              inputMode="numeric"
              maxLength={10}
              disabled={
                busy ||
                !preview.canApply ||
                reloadRequired ||
                !!(preview.sellerProblems && preview.sellerProblems.length)
              }
              onChange={(_, data) => setAmount(String(data.value))}
            />
            <Form.Input
              label="Количество баллов АРТ (необязательно)"
              value={adminAmount}
              inputMode="numeric"
              maxLength={10}
              disabled={
                busy ||
                !preview.canApply ||
                reloadRequired ||
                !!(preview.adminProblems && preview.adminProblems.length)
              }
              placeholder="Пустое поле — без начисления АРТ"
              onChange={(_, data) => setAdminAmount(String(data.value))}
            />
            <Button
              primary
              type="submit"
              loading={busy}
              disabled={
                busy || !preview.canApply || reloadRequired || !validAmount
              }
            >
              Начислить баллы
            </Button>
          </Form>
          <Button basic disabled={busy} onClick={() => load(preview.sale.id)}>
            Обновить данные
          </Button>
          <Button
            basic
            disabled={busy}
            onClick={() => {
              setPreview(null);
              setSales([]);
              setSearched(false);
              setError('');
              setResult('');
            }}
          >
            Другая продажа
          </Button>
        </>
      )}
    </section>
  );
}
